import React, { useEffect, useState } from 'react';
import style from '../../../../assets/styles/reportGenerate.module.css';
import AuthLayout from '../../../../layout/AuthLayout';
import RightPanelTopMenu from '../../common/RightPanelTopMenu';
import downloadIcon from '../../../../assets/images/Downlaod.svg';
import downArrowBtn from '../../../../assets/images/Group.svg';
import rightArrowBtn from '../../../../assets/images/active.svg';
import downBtn from '../../../../assets/images/downArrow1.svg';
import blueRightBtn from '../../../../assets/images/blueRightArrow.svg';
import { useNavigate, useParams } from 'react-router-dom';
import VerticalLine from '../../../../assets/images/vertical-line.svg';
import bulletPoint from '../../../../assets/images/bulletpoints.svg';
import ReportFilterModal from '../../../atoms/Modal/ReportFilterModal';
import { useToast } from '../../common/Toast';
import axios from 'axios';
import { GET_REPORT_DOWNLOAD } from '../../../../action/apiPath';
import Cookies from 'js-cookie';
import { GetReportDetailList, GetReportQuestionBank, flushReportData } from '../../../../redux/action';
import { useDispatch, useSelector } from 'react-redux';
// Interfaces
interface LodCount {
  lod_id: number;
  lod_name: string;
  total_questions: number;
}
interface SubTopic {
  name: string;
  lod_count: LodCount[];
}

interface Topic {
  name: string;
  lod_count: LodCount[];
  subtopic: SubTopic[];
}

interface Area {
  name: string;
  lod_count: LodCount[];
  topic: Topic[];
}

interface Subject {
  name: string;
  lod_count: LodCount[];
  area: Area[];
}
interface SortConfig {
  field: number | null;
  direction: 'asc' | 'desc';
}

// New interface for tracking expansion state
interface ExpansionState {
  subjects: Record<number, boolean>;
  areas: Record<string, boolean>;
  topics: Record<string, boolean>;
}

const ReportGenerate: React.FC = () => {
  const toast = useToast();
  const [expandAll, setExpandAll] = useState(false);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [reportDetails, setReportDetails] = useState<Subject[]>([]);
  const [lodTypes, setLodTypes] = useState<LodCount[]>([]);
  const dispatch = useDispatch();
  const { id } = useParams();
  const { viewReportDetails } = useSelector((state: any) => state.GetAllData);
  const [organizationId, setOrganizationId] = useState<number | null>(() => {
    const storedId = localStorage.getItem('selectedOrgId');
    return storedId ? Number(storedId) : null;
  });
  useEffect(() => {
    if (id) {
      setIsLoading(true);
      dispatch(GetReportDetailList(organizationId, id));
    }
  }, [id, organizationId, dispatch]);

  useEffect(() => {
    if (viewReportDetails?.data) {
      setIsLoading(false);
      setReportDetails(viewReportDetails.data);
      setTotalCount(viewReportDetails.data.length);
      if (viewReportDetails.data.length > 0) {
        const subject = viewReportDetails.data[0];
        setLodTypes(subject.lod_count);
      }
    }
  }, [id, viewReportDetails, dispatch]);
  // New state for tracking expansion
  const [expansionState, setExpansionState] = useState<ExpansionState>(() => {
    const initialState: ExpansionState = {
      subjects: {},
      areas: {},
      topics: {},
    };

    // Set default expanded state for subjects and areas
    reportDetails?.forEach((_, index) => {
      initialState.subjects[index] = true; // subjects expanded by default
    });

    const initializeAreas = (subject: Subject, sIndex: number) => {
      subject.area?.forEach((area, aIndex) => {
        const areaKey = `${sIndex}-${aIndex}`;
        initialState.areas[areaKey] = false; // areas collapsed by default
        initializeTopics(sIndex, aIndex, area);
      });
    };

    const initializeTopics = (sIndex: number, aIndex: number, area: Area) => {
      area.topic?.forEach((_, tIndex) => {
        const topicKey = `${sIndex}-${aIndex}-${tIndex}`;
        initialState.topics[topicKey] = false; // topics collapsed by default
      });
    };

    reportDetails?.forEach((subject, sIndex) => {
      initializeAreas(subject, sIndex);
    });

    return initialState;
  });

  useEffect(() => {
    if (!reportDetails || reportDetails.length === 0) return;

    setExpansionState((prevState) => {
      const newState = { ...prevState };

      // Expand subjects by default
      reportDetails.forEach((_, index) => {
        newState.subjects[index] = true;
      });

      // Initialize areas and topics
      reportDetails.forEach((subject, sIndex) => {
        initializeAreasAndTopics(subject, sIndex, newState);
      });

      return newState;
    });
  }, [reportDetails]);

  const initializeAreasAndTopics = (subject: Subject, sIndex: number, newState: any) => {
    subject.area?.forEach((area, aIndex) => {
      const areaKey = `${sIndex}-${aIndex}`;

      // Only set if not already set
      if (newState.areas[areaKey] === undefined) {
        newState.areas[areaKey] = false;
      }

      initializeTopics(area, sIndex, aIndex, newState);
    });
  };

  const initializeTopics = (area: Area, sIndex: number, aIndex: number, newState: any) => {
    area.topic?.forEach((_, tIndex) => {
      const topicKey = `${sIndex}-${aIndex}-${tIndex}`;

      // Only set if not already set
      if (newState.topics[topicKey] === undefined) {
        newState.topics[topicKey] = false;
      }
    });
  };

  // Toggle expand/collapse functionality
  const toggleExpand = (subjectIndex: number, areaIndex?: number, topicIndex?: number) => {
    setExpansionState((prevState) => {
      const newState = { ...prevState };

      if (areaIndex === undefined) {
        // Toggle subject
        const key = subjectIndex;
        newState.subjects[key] = !prevState.subjects[key];
      } else if (topicIndex === undefined) {
        // Toggle area
        const key = `${subjectIndex}-${areaIndex}`;
        newState.areas[key] = !prevState.areas[key];
      } else {
        // Toggle topic
        const key = `${subjectIndex}-${areaIndex}-${topicIndex}`;
        newState.topics[key] = !prevState.topics[key];
      }

      return newState;
    });
  };

  // Function to handle opening the modal
  const handleOpenModal = () => {
    setIsModalOpen(true);
    dispatch(GetReportQuestionBank(organizationId));
  };

  // Function to handle closing the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    navigate('/reports-history');
  };

  // Sorting functions - Add a sorting configuration state
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: null,
    direction: 'asc',
  });
  // Helper function to get total_questions for a given lod_id
  const getLodQuestions = (lodCounts: LodCount[], lodId: number): number => {
    const lodItem = lodCounts.find((item) => item.lod_id === lodId);
    return lodItem ? lodItem.total_questions : 0;
  };

  // Typed sorting handler function
  const handleSort = (lodId: number) => {
    setSortConfig((prevConfig) => {
      let direction: 'asc' | 'desc';

      if (prevConfig.field === lodId) {
        // Toggle direction if same field is clicked
        direction = prevConfig.direction === 'asc' ? 'desc' : 'asc';
      } else {
        // Default to ascending for a new field
        direction = 'asc';
      }

      const sortedReportDetails = [...reportDetails].sort((a, b) => {
        const aValue = getLodQuestions(a.lod_count, lodId);
        const bValue = getLodQuestions(b.lod_count, lodId);

        if (direction === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      });
      setReportDetails(sortedReportDetails);
      return { field: lodId, direction };
    });
  };

  // Helper function to get rotation with proper typing
  const getRotation = (lodId: number) => {
    if (sortConfig.field !== lodId) return 'rotate(0deg)';
    return sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'rotate(0deg)';
  };

  // Helper function to get icon color with proper typing
  const getIconColor = (lodId: number) => {
    return sortConfig.field === lodId ? '#00ABFB' : '#999090';
  };

  // Update the renderAreas function
  const renderAreas = (areas: Area[], subjectIndex: number) => {
    return areas.map((area, aIndex) => (
      <React.Fragment key={`area-${aIndex + 1}`}>
        <tr>
          <td>
            <span className={style.tdSubjectWrap}>
              <button onClick={() => toggleExpand(subjectIndex, aIndex)} className={style.areaToggle}>
                <span className={style.spanWrap}>
                  <img
                    src={expansionState.areas[`${subjectIndex}-${aIndex}`] ? downBtn : rightArrowBtn}
                    alt="arrow"
                    className={style.subjectToggleArrow}
                  />
                  {area?.name}
                </span>
              </button>
            </span>
          </td>
          {lodTypes.map((lod) => (
            <td key={`area-${aIndex}-lod-${lod.lod_id}`}>
              <span className={style.tdSpanWrap}>{getLodQuestions(area.lod_count, lod.lod_id)}</span>
            </td>
          ))}
        </tr>
        {expansionState.areas[`${subjectIndex}-${aIndex}`] && renderTopics(area.topic, subjectIndex, aIndex)}
      </React.Fragment>
    ));
  };

  // Update the renderTopics function
  const renderTopics = (topics: Topic[], subjectIndex: number, areaIndex: number) => {
    return topics.map((topic, tIndex) => (
      <React.Fragment key={`topic-${tIndex + 1}`}>
        <tr>
          <td>
            <span className={style.tdSubjectWrap}>
              <button
                onClick={() => toggleExpand(subjectIndex, areaIndex, tIndex)}
                className={`${style.areaToggle} ${style.topicToggle}`}
              >
                <span className={style.spanWrap}>
                  <img
                    src={expansionState.topics[`${subjectIndex}-${areaIndex}-${tIndex}`] ? downBtn : rightArrowBtn}
                    alt="arrow"
                    className={style.subjectToggleArrow}
                  />
                  {topic.name}
                </span>
              </button>
            </span>
          </td>
          {lodTypes.map((lod) => (
            <td key={`topic-${tIndex}-lod-${lod.lod_id}`}>
              <span className={style.tdSpanWrap}>{getLodQuestions(topic.lod_count, lod.lod_id)}</span>
            </td>
          ))}
        </tr>
        {expansionState?.topics[`${subjectIndex}-${areaIndex}-${tIndex}`] && renderSubTopics(topic.subtopic)}
      </React.Fragment>
    ));
  };

  // Update the renderSubTopics function
  const renderSubTopics = (subTopics: SubTopic[]) => {
    return subTopics.map((subTopic, stIndex) => (
      <tr key={`subtopic-${stIndex + 1}`}>
        <td>
          <span className={style.subTopicWrap}>
            <img src={bulletPoint} className={style.bulletPoint} alt="Vertical Line" />
            <span className={style.spanWrap} style={{ marginLeft: '12px' }}>
              {subTopic.name}
            </span>
          </span>
        </td>
        {lodTypes.map((lod) => (
          <td key={`subtopic-${stIndex}-lod-${lod.lod_id}`}>
            <span className={style.tdSpanWrap}>{getLodQuestions(subTopic.lod_count, lod.lod_id)}</span>
          </td>
        ))}
      </tr>
    ));
  };

  const handleExpandAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const shouldExpand = e.target.checked;
    setExpandAll(shouldExpand);

    setExpansionState((prevState) => {
      const newState = { ...prevState };

      // Expand subjects
      reportDetails?.forEach((subject, sIndex) => {
        newState.subjects[sIndex] = shouldExpand;
      });

      // Expand areas and topics
      const expandAreasAndTopics = (subject: Subject, sIndex: number) => {
        subject.area?.forEach((area, aIndex) => {
          const areaKey = `${sIndex}-${aIndex}`;
          newState.areas[areaKey] = shouldExpand;

          expandTopics(area, sIndex, aIndex);
        });
      };

      const expandTopics = (area: Area, sIndex: number, aIndex: number) => {
        area.topic?.forEach((_, tIndex) => {
          const topicKey = `${sIndex}-${aIndex}-${tIndex}`;
          newState.topics[topicKey] = shouldExpand;
        });
      };

      reportDetails?.forEach((subject, sIndex) => {
        expandAreasAndTopics(subject, sIndex);
      });

      return newState;
    });
  };

  const handleDownloadClick = async () => {
    const userInfo = localStorage?.getItem('userInfo');
    const jwt = userInfo ? JSON.parse(userInfo).jwt : '';
    try {
      const response = await axios({
        url: `${GET_REPORT_DOWNLOAD}${organizationId}/?report_id=${id}`,
        method: 'GET',
        responseType: 'blob',
        headers: {
          Authorization: `jwt ${jwt ?? Cookies.get('accessToken')}`, // Add the authentication header
        },
      });
      const contentDisposition = response.headers['Content-Disposition'] || response.headers['content-disposition'];

      // Extract file name from Content-Disposition
      let fileName = 'default_filename'; // Fallback filename
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=(['"]?)(.*?)(\1|$)/);
        if (fileNameMatch?.[2]) {
          fileName = fileNameMatch[2]; // Extracted file name
        }
      }

      // Create a blob and trigger file download
      const contentType = response.headers['content-type'];
      const blob = new Blob([response.data], {
        type: typeof contentType === 'string' && contentType ? contentType : 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Download Error:', error);
      if (toast) {
        toast.add('Failed to download file. Please try again.', 'error');
      }
    }
  };
  const handleBackbutton = () => {
    navigate('/reports-history');
    dispatch(flushReportData());
  };
  return (
    <AuthLayout>
      <RightPanelTopMenu
        setQuestionBankId={null}
        title="Question Distribution Report"
        setOrganisationId={(id: number) => setOrganizationId(id)}
      />
      <div className={style.reportGenerate_table_top}>
        <button
          className={style.left}
          onClick={handleBackbutton}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleBackbutton();
          }}
          style={{ padding: '0px', background: 'none', border: 'none' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M15.5488 19.422L9.02883 12.902C8.25883 12.132 8.25883 10.872 9.02883 10.102L15.5488 3.58203"
              stroke="#212529"
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span style={{ position: 'relative', bottom: '5px', fontSize: '16px', textDecoration: 'underline' }}>
            Back
          </span>
        </button>

        <div className={`${style.table_content_report} ${style.report_table_content}`}>
          <div className={style.top_content_table}>
            <div className={style.top_content_tableLeft}>
              <div>Question Distribution Report</div>
              <button
                className={`${style.left} ${style.reportHistoryLink}`}
                onClick={handleBackbutton}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleBackbutton();
                  }
                }}
              >
                View Report History
              </button>
            </div>
            <div className={style.top_content_tableRight}>
              <div className={style.checkboxtags}>
                <label
                  className="custom-checkbox"
                  htmlFor="expandAllCheckbox"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <input type="checkbox" id="expandAllCheckbox" checked={expandAll} onChange={handleExpandAll} />
                  <span className="checkmark" style={{ marginRight: '6px' }}></span>
                  <span className={style.checkboxLabel}>Expand All</span>
                </label>
              </div>

              <img src={VerticalLine} className={style.vertical_line} alt="Vertical Line" />
              <div className={`${style.filter} ${style.report_filter}`}>
                <button
                  className={`${style.pointer} ${style.filterBtnwrap}`}
                  onClick={handleOpenModal}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleOpenModal()}
                  aria-label="Open filter modal"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5.39844 2.10156H18.5984C19.6984 2.10156 20.5984 3.00156 20.5984 4.10156V6.30156C20.5984 7.10156 20.0984 8.10156 19.5984 8.60156L15.2984 12.4016C14.6984 12.9016 14.2984 13.9016 14.2984 14.7016V19.0016C14.2984 19.6016 13.8984 20.4016 13.3984 20.7016L11.9984 21.6016C10.6984 22.4016 8.89844 21.5016 8.89844 19.9016V14.6016C8.89844 13.9016 8.49844 13.0016 8.09844 12.5016L4.29844 8.50156C3.79844 8.00156 3.39844 7.10156 3.39844 6.50156V4.20156C3.39844 3.00156 4.29844 2.10156 5.39844 2.10156Z"
                      stroke="#212529"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span style={{ fontSize: '14px' }}>Filter</span>
                </button>
              </div>

              <div className={style.add_question}>
                <button className={`${style.add_new_question} ${style.pointer}`} onClick={handleDownloadClick}>
                  <img src={downloadIcon} alt="Download" />
                  <span>Download Report</span>
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          {totalCount !== 0 ? (
            <div className={style.sub_head_content_table}>
              <table>
                <thead>
                  <tr>
                    <th className={`${style.first} ${style.pointer}`}>
                      <span>Subject | Area | Topic | Sub-Topic</span>
                    </th>
                    {lodTypes.map((lod) => (
                      <th
                        key={`header-lod-${lod.lod_id}`}
                        className={`${style.second} ${style.pointer}`}
                        onClick={() => handleSort(lod.lod_id)}
                      >
                        <span style={{ cursor: 'pointer' }}>
                          {lod.lod_name.charAt(0).toUpperCase() + lod.lod_name.slice(1)}
                        </span>
                        <svg
                          width="19"
                          height="12"
                          viewBox="0 0 19 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{
                            marginLeft: '6px',
                            transform: getRotation(lod.lod_id),
                            transition: 'transform 0.3s ease',
                            cursor: 'pointer',
                          }}
                        >
                          <path
                            d="M15.2308 2.36369L15.2308 11.3077C15.2308 11.69 14.9208 12 14.5385 12C14.1561 12 13.8462 11.69 13.8462 11.3077L13.8462 2.36369L11.7972 4.41261C11.5269 4.68298 11.0885 4.68298 10.8182 4.41261C10.5478 4.14225 10.5478 3.7039 10.8182 3.43354L14.0489 0.202771C14.3193 -0.0675907 14.7576 -0.0675907 15.028 0.202771L18.2588 3.43354C18.5291 3.7039 18.5291 4.14225 18.2588 4.41261C17.9884 4.68298 17.5501 4.68298 17.2797 4.41261L15.2308 2.36369Z"
                            fill={getIconColor(lod.lod_id)}
                          />
                          <path
                            d="M0 10.8462C0 11.2285 0.309957 11.5385 0.692308 11.5385H9.46154C9.84389 11.5385 10.1538 11.2285 10.1538 10.8462C10.1538 10.4638 9.84389 10.1538 9.46154 10.1538H0.692308C0.309957 10.1538 0 10.4638 0 10.8462Z"
                            fill={getIconColor(lod.lod_id)}
                          />
                          <path
                            d="M0 6.23077C0 6.61312 0.309957 6.92308 0.692308 6.92308H5.76923C6.15158 6.92308 6.46154 6.61312 6.46154 6.23077C6.46154 5.84842 6.15158 5.53846 5.76923 5.53846H0.692308C0.309957 5.53846 0 5.84842 0 6.23077Z"
                            fill={getIconColor(lod.lod_id)}
                          />
                          <path
                            d="M0 1.61538C0 1.99774 0.309957 2.30769 0.692308 2.30769H3.92308C4.30543 2.30769 4.61538 1.99774 4.61538 1.61538C4.61538 1.23303 4.30543 0.923077 3.92308 0.923077H0.692308C0.309957 0.923077 0 1.23303 0 1.61538Z"
                            fill={getIconColor(lod.lod_id)}
                          />
                        </svg>
                      </th>
                    ))}
                  </tr>
                </thead>

                {isLoading ? (
                  <tbody>
                    <tr>
                      <td
                        colSpan={lodTypes.length + 1}
                        style={{
                          textAlign: 'center',
                          padding: '20px',
                          fontSize: '15px',
                          fontFamily: 'IBM Plex Sans',
                        }}
                      >
                        Loading...
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {reportDetails?.map((subject, sIndex) => (
                      <React.Fragment key={`subject-${sIndex + 1}`}>
                        <tr className={`${style.table_body_content}`}>
                          <td className={style.subjectColumn}>
                            <span className={style.tdSubjectWrap}>
                              <button onClick={() => toggleExpand(sIndex)} className={style.subjectToggle}>
                                <span className={style.spanWrap}>
                                  <img
                                    src={expansionState.subjects[sIndex] ? downArrowBtn : blueRightBtn}
                                    alt="arrow"
                                    className={style.subjectToggleArrow}
                                  />
                                  {subject?.name}
                                </span>
                              </button>
                            </span>
                          </td>
                          {lodTypes.map((lod) => (
                            <td key={`subject-${sIndex}-lod-${lod.lod_id}`} className={style.subjectColumn}>
                              <span className={style.tdSpanWrap}>{getLodQuestions(subject.lod_count, lod.lod_id)}</span>
                            </td>
                          ))}
                        </tr>
                        {expansionState.subjects[sIndex] && renderAreas(subject.area, sIndex)}
                      </React.Fragment>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          ) : (
            <div
              className={style.noData}
              style={{
                textAlign: 'center',
                padding: '20px',
                fontSize: '15px',
                fontFamily: 'IBM Plex Sans',
              }}
            >
              <div className={style.noDataText}>No Results Found.</div>
            </div>
          )}
        </div>
        <ReportFilterModal isOpen={isModalOpen} onClose={handleCloseModal} />
      </div>
    </AuthLayout>
  );
};

export default ReportGenerate;
